import { useQuery } from '@tanstack/react-query';
import { type CruisePhoto } from '@utazzo/shared';

import { resolveMedia } from '@/features/website-cms/components/media.api';

/**
 * Cruise cover-photo resolution for admin thumbnails (list card, day-board
 * card, change dialog). Photos are stored as `{ mediaId }` (uploaded to S3) or
 * `{ url }` (legacy import); the cover is sortOrder 0, else the first photo.
 * mediaId covers resolve to a presigned URL via the media API (cached by
 * react-query); legacy URL covers are used directly. Callers render the URL
 * when present and fall back to an icon otherwise.
 */

/** The cover photo of a cruise: sortOrder 0, else the first photo. */
export function pickCoverPhoto(photos: unknown): CruisePhoto | null {
  if (!Array.isArray(photos) || photos.length === 0) return null;
  const list = photos as CruisePhoto[];
  return list.find((p) => (p.sortOrder ?? 0) === 0) ?? list[0] ?? null;
}

/**
 * Resolve a cruise's cover photo to a preview URL. Returns null when there is
 * no cover or while a mediaId cover is still resolving, so the caller shows its
 * icon fallback in the meantime.
 */
export function useCruiseCoverUrl(photos: unknown): string | null {
  const cover = pickCoverPhoto(photos);
  const mediaId = cover?.mediaId ?? null;
  const legacyUrl = cover?.url && !cover.mediaId ? cover.url : null;
  const { data } = useQuery({
    queryKey: ['media-resolve', mediaId],
    queryFn: () => resolveMedia(mediaId as string),
    enabled: Boolean(mediaId),
    staleTime: 5 * 60 * 1000,
  });
  return legacyUrl ?? data?.url ?? null;
}
